import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  loadPublicBookedDatesTicker,
  tryLoadBookingsFromFirestore,
} from '../utils/firestore';
import './BookedDatesTicker.css';

/**
 * Extracts and formats event dates from bookings (no PII).
 * Returns sorted, unique date strings for display.
 */
function getBookedDatesFromBookings(bookings) {
  const dateSet = new Set();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  bookings.forEach((booking) => {
    if (booking.eventType === 'multi-day' && Array.isArray(booking.dayLocations)) {
      booking.dayLocations.forEach((day) => {
        if (day.date) dateSet.add(day.date);
      });
    } else if (booking.eventDate) {
      dateSet.add(booking.eventDate);
    }
  });

  const dates = Array.from(dateSet)
    .map((d) => {
      const parsed = new Date(d);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    })
    .filter(Boolean)
    .sort((a, b) => a - b);

  const formatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return dates
    .filter((d) => d >= today)
    .map((d) => formatter.format(d));
}

function formatIsoDatesForTicker(isoDates) {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return isoDates
    .map((iso) => {
      const d = new Date(`${iso}T12:00:00`);
      if (Number.isNaN(d.getTime()) || d < today) return null;
      return formatter.format(d);
    })
    .filter(Boolean);
}

/** @typedef {'marquee' | 'empty' | 'neutral'} TickerMode */

const BookedDatesTicker = () => {
  const [dates, setDates] = useState([]);
  /** neutral = could not load (e.g. not signed in + no public doc); do not claim “no bookings” */
  const [mode, setMode] = useState(/** @type {TickerMode} */ ('marquee'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        const pub = await loadPublicBookedDatesTicker();
        if (!mounted) return;
        if (pub) {
          const formatted = formatIsoDatesForTicker(pub.dates);
          setDates(formatted);
          setMode(formatted.length === 0 ? 'empty' : 'marquee');
          setLoading(false);
          return;
        }
      } catch {
        /* fall through */
      }

      const { ok, bookings } = await tryLoadBookingsFromFirestore();
      if (!mounted) return;
      if (ok) {
        const d = getBookedDatesFromBookings(bookings);
        setDates(d);
        setMode(d.length === 0 ? 'empty' : 'marquee');
      } else {
        setDates([]);
        setMode('neutral');
      }
      setLoading(false);
    };

    run();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null;

  if (mode === 'neutral') {
    return (
      <Link
        to="/book"
        className="booked-dates-ticker booked-dates-ticker--empty booked-dates-ticker--neutral"
      >
        <span className="booked-dates-ticker-text">
          Book your date - check availability on the booking page. Popular weekends fill fast.
        </span>
      </Link>
    );
  }

  if (mode === 'empty') {
    return (
      <Link to="/book" className="booked-dates-ticker booked-dates-ticker--empty">
        <span className="booked-dates-ticker-text">
          No dates booked yet – your date might be free! Book now.
        </span>
      </Link>
    );
  }

  const text = `We're booked on: ${dates.join('  •  ')} - Book your date early!`;
  const duplicated = `${text}  •  ${text}`;

  return (
    <Link
      to="/book"
      className="booked-dates-ticker"
      role="marquee"
      aria-live="polite"
      aria-label="Booked dates - tap to book your date"
    >
      <div className="booked-dates-ticker-inner">
        <span className="booked-dates-ticker-text">{duplicated}</span>
      </div>
    </Link>
  );
};

export default BookedDatesTicker;
